import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getRoleRecommendation, getRoleEntityAccess } from "../../../../API/AliceGPT/AliceGPT";
import { Checkbox } from "@progress/kendo-react-inputs";
import CreateRoleDialog from "./CreateRoleDialog";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import ShuffleMessage from "../../../Shared/ShuffleMessage/ShuffleMessage";
import { messagesWhenCreatingProject } from "../../../Shared/ShuffleMessage/MessagesHelper";

const CARD_COLORS = [
    "#fef2f2",
    "#eff6ff",
    "#f0fdf4",
    "#f2fef9",
    "#f2fcfe",
    "#f2f4fe",
    "#f7f2fe",
    "#f2fefe",
];

const DEFAULT_ROLES = [
  {
    key: "admin",
    title: "default_roles.admin.title",
    description: "default_roles.admin.description",
    color: CARD_COLORS[0],
  },
  {
    key: "contributor",
    title: "default_roles.contributor.title",
    description: "default_roles.contributor.description",
    color: CARD_COLORS[1],
  },
  {
    key: "guest",
    title: "default_roles.guest.title",
    description: "default_roles.guest.description",
    color: CARD_COLORS[2],
  },
];

const RoleRecommendation = ({ entities = [], maxRoles = 6, descriptionRef, onNext, onBack, onCreateApp }) => {
    const { t } = useTranslation("role_recommendation", { useSuspense: false });
    const [state, dispatch] = useAliceGPT();

    useEffect(() => {
        debugger;
        if (state.shouldLoadRoles && state.subscription.rolesPermission === "Full access") {
            dispatch({ type: "ROLES_LOADING", payload: true });
            const entityNames = entities.filter(e => e.checked).map((e) => e.name);
            getRoleRecommendation(entityNames, maxRoles, descriptionRef?.current)
                .then((res) => {
                    if (res && Array.isArray(res) && res.length > 0) {
                        const recommendedRoles = res.map((r, i) => ({
                            key: r.roleName.toLowerCase(),
                            title: r.roleName,
                            description: r.description,
                            color: CARD_COLORS[(i + 6) % CARD_COLORS.length],
                            entities: r.entities || [],
                        }));
                        dispatch({ type: "ROLES_UPDATED", payload: recommendedRoles });
                        dispatch({ type: "SELECTED_ROLES_UPDATED", payload: recommendedRoles.slice(0, 3).map(r => r.key) });
                    }
                })
                .finally(() => dispatch({ type: "ROLES_LOADING", payload: false }));
        }
    }, [entities, maxRoles, dispatch]);

    const handleSelect = (key) => {
        // Prevent unchecking of admin, contributor, and guest
        if (["admin", "contributor", "guest"].includes(key)) return;
        const prev = state.selectedRoles || [];
        const updated = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
        dispatch({ type: "SELECTED_ROLES_UPDATED", payload: updated });
    };

    const handleAddCustomRole = async (roleName) => {
        const customKey = roleName.toLowerCase().replace(/\s+/g, "_");
        if ((state.roles || []).some(r => r.key === customKey)) return;
        // Get checked entity names
        const entityNames = entities.filter(e => e.checked).map((e) => e.name);
        // Get description from ref if available
        const description = descriptionRef?.current;
        // Call API to get recommended entities for this role
        let entitiesForRole = [];
        try {
            const res = await getRoleEntityAccess(roleName, entityNames, description);
            if (res && Array.isArray(res)) {
                entitiesForRole = res;
            } else {
                // fallback: set all selected entities to full access
                entitiesForRole = entityNames.map(entityName => ({
                    entityName,
                    accessLevel: "Full access"
                }));
            }
        } catch (e) {
            // fallback: set all selected entities to full access
            entitiesForRole = entityNames.map(entityName => ({
                entityName,
                accessLevel: "Full access"
            }));
        }
        const newRole = {
            key: customKey,
            title: roleName,
            description: "",
            color: CARD_COLORS[5],
            entities: entitiesForRole,
        };
        dispatch({ type: "ROLES_UPDATED", payload: [...(state.roles || []), newRole] });
        dispatch({ type: "SELECTED_ROLES_UPDATED", payload: [...(state.selectedRoles || []), customKey] });
        dispatch({ type: "SHOW_ROLE_DIALOG", payload: false });
    };

    if (state.rolesLoading) {
        return (
            <div className='d-flex flex-column justify-content-center align-items-center h-100'>
                <img src={getDatalakeImageUrl('loaders/RecommendationLoader.gif')} alt="" />
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        )
    }

    return (
        <div style={{ padding: 32 }}>
            <CreateRoleDialog
                open={!!state.showRoleDialog}
                allowCreate={state.subscription.rolesPermission === "Full access"}
                onClose={() => dispatch({ type: "SHOW_ROLE_DIALOG", payload: false })}
                onCreate={handleAddCustomRole}
            />
            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                    <div className="w-25">
                        <h4 style={{ margin: 0, display: "inline-block", fontWeight: 500 }}>{t('title')}</h4>
                        <span style={{ marginLeft: 8, color: "#888", cursor: "pointer" }} title={t('info_tooltip')}>
                            <i className="fas fa-info-circle font-size-3"></i>
                        </span>
                    </div>
                    <div>
                        <button className="btn btn-primary" onClick={() => dispatch({ type: "SHOW_ROLE_DIALOG", payload: true })}>
                            <i className="fas fa-plus"></i>
                            &nbsp;
                            {t('new_role_button')}
                        </button>
                    </div>
                </div>
                
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
                        {/* Always show admin, contributor, guest as first three roles, uncheckable */}
                        {["admin", "contributor", "guest"].map((key, idx) => {
                            const role = (state.roles || []).find(r => r.key === key) || DEFAULT_ROLES[idx];
                            return (
                                <div
                                    key={role.key}
                                    style={{
                                        border: "2px solid #3B82F6",
                                        borderRadius: 12,
                                        padding: '18px',
                                        minHeight: 150,
                                        boxSizing: "border-box",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-start",
                                        position: "relative",
                                        cursor: "not-allowed",
                                        opacity: 1
                                    }}
                                    onClick={() => {}}
                                >
                                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{t(role.title)}</div>
                                            <div style={{ color: "#444", fontSize: 13, fontWeight: 400 }}>{t(role.description)}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {/* Render the rest of the roles, excluding admin, contributor, guest */}
                        {(state.roles || []).filter(r => !["admin", "contributor", "guest"].includes(r.key)).map((role, idx) => (
                            <div
                                key={role.key}
                                style={{
                                    border: (state.selectedRoles || []).includes(role.key) ? "2px solid #3B82F6" : "1.5px solid #eee",
                                    borderRadius: 12,
                                    padding: '18px',
                                    minHeight: 150,
                                    boxSizing: "border-box",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-start",
                                    position: "relative",
                                    cursor: "pointer"
                                }}
                                onClick={() => handleSelect(role.key)}
                            >
                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{role.title}</div>
                                        <div style={{ color: "#444", fontSize: 13, fontWeight: 400 }}>{role.description}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                
            </div>
        </div>
    );
};

export default RoleRecommendation;
